<template>
  <header class="bg-white border-b border-gray-100 flex justify-between items-center px-6 py-3 shrink-0 h-16">
    <div class="flex items-center space-x-6 flex-1">
      
      <!-- Mobile menu button -->
      <button @click="$emit('toggle-sidebar')" class="md:hidden text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
      </button>

      <!-- Search -->
      <div class="relative w-full max-w-md hidden md:block">
        <span class="absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
        </span>
        <input type="text" placeholder="Search files, users, transfers..." class="w-full bg-white text-gray-900 border border-gray-200 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm placeholder-gray-400 transition shadow-sm">
      </div>
    </div>

    <!-- Right Side Actions -->
    <div class="flex items-center space-x-6">
      
      <!-- Network Status -->
      <div class="hidden lg:flex items-center space-x-6 text-xs font-medium border-r border-gray-100 pr-6">
        <div class="flex flex-col items-center">
          <div class="flex items-center text-green-500 font-bold">
            <span class="h-2 w-2 rounded-full bg-green-500 mr-1.5"></span>
            LAN Connected
          </div>
          <span class="text-[10px] text-gray-500">192.168.1.10</span>
        </div>
        <div class="flex flex-col items-center">
          <div class="flex items-center text-green-500 font-bold">
            <span class="h-2 w-2 rounded-full bg-green-500 mr-1.5"></span>
            Internet Online
          </div>
          <span class="text-[10px] text-gray-500">{{ currentHost }}</span>
        </div>
      </div>

      <!-- Notifications -->
      <div class="relative group">
        <button class="relative text-gray-400 hover:text-gray-600 transition">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg>
          <span v-if="notificationStore.unreadCount > 0" class="absolute top-0 right-0 -mt-1 -mr-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold ring-2 ring-white">{{ notificationStore.unreadCount }}</span>
        </button>

        <!-- Dropdown Menu -->
        <div class="absolute right-0 mt-4 w-80 bg-white border border-gray-100 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all z-[60] flex flex-col max-h-[400px]">
          <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 rounded-t-lg">
            <h3 class="font-bold text-gray-900 text-sm">Notifications</h3>
            <button v-if="notificationStore.unreadCount > 0" @click="notificationStore.markAsRead('all')" class="text-xs text-[#4318FF] hover:text-[#3311CC] font-medium cursor-pointer">Mark all read</button>
          </div>
          <div class="flex-1 overflow-y-auto">
            <div v-if="notificationStore.notifications.length === 0" class="px-4 py-6 text-center text-sm text-gray-500">
              No recent notifications.
            </div>
            <div v-else v-for="notif in notificationStore.notifications" :key="notif.id" 
                 class="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex space-x-3 cursor-pointer"
                 :class="{ 'bg-blue-50/20': !notif.read_at }"
                 @click="notificationStore.markAsRead(notif.id)">
              <div class="flex-shrink-0 mt-0.5">
                <div class="w-8 h-8 rounded-full flex items-center justify-center text-white" :style="{ backgroundColor: notif.data?.color || '#4318FF' }">
                   <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-gray-900 leading-snug">{{ notif.data?.title }}</p>
                <p class="text-xs text-gray-500 truncate mt-0.5">{{ notif.data?.description }}</p>
              </div>
              <div v-if="!notif.read_at" class="flex-shrink-0 flex items-center">
                <div class="w-2 h-2 bg-blue-600 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- User Dropdown -->
      <div class="relative group cursor-pointer">
        <div class="flex items-center space-x-3">
          <img :src="userAvatar" alt="Avatar" class="w-8 h-8 rounded-full border border-gray-200 object-cover">
          <div class="hidden md:block text-sm text-right">
            <p class="text-gray-900 font-bold leading-tight">{{ userName }}</p>
            <p class="text-gray-500 text-[11px] uppercase tracking-wider font-semibold">{{ userRole }}</p>
          </div>
          <svg class="w-4 h-4 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>
        </div>

        <!-- Dropdown Menu -->
        <div class="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-lg shadow-xl py-1 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all z-50">
          <router-link to="/profile" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">My Profile</router-link>
          <router-link :to="authStore.isAdmin ? '/settings' : '/profile?tab=preferences'" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Settings</router-link>
          <div class="border-t border-gray-100 my-1"></div>
          <button @click="logout" class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50">Sign out</button>
        </div>
      </div>

    </div>
  </header>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '../store/auth';
import { useNotificationStore } from '../store/notifications';
import { useRouter } from 'vue-router';

const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const router = useRouter();

onMounted(() => {
    notificationStore.startPolling(30000);
});

onUnmounted(() => {
    notificationStore.stopPolling();
});

const userName = computed(() => authStore.user?.name || 'Admin');
const currentHost = computed(() => typeof window !== 'undefined' ? window.location.hostname : '');
const userAvatar = computed(() => {
  if (authStore.user?.avatar) return authStore.user.avatar;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(authStore.user?.name || 'User')}&color=4318FF&background=EEF2FF`;
});
const userRole = computed(() => {
    if (authStore.user?.roles?.some(r => r.name === 'super_admin')) return 'Super Admin';
    return 'User';
});

const logout = () => {
    authStore.logout();
    router.push('/login');
};
</script>
